import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Pencil, Mail, FileText, ExternalLink } from "lucide-react";
import { api, LOGO } from "@/lib/data";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";

const emptyArticle = { title: "", excerpt: "", body: "", category: "Insights", cover_image: "", author: "IPNOMICS LLP", published: true };

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyArticle);

  const load = useCallback(async () => {
    try {
      const [a, c] = await Promise.all([api.get("/articles?all=true"), api.get("/admin/contacts")]);
      setArticles(a.data); setContacts(c.data);
    } catch { logout(); navigate("/admin/login"); }
  }, [logout, navigate]);

  useEffect(() => {
    api.get("/auth/me").then(load).catch(() => { logout(); navigate("/admin/login"); });
  }, [load, logout, navigate]);

  const openNew = () => { setEditing(null); setForm(emptyArticle); setDialogOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...a }); setDialogOpen(true); };

  const saveArticle = async () => {
    if (!form.title || !form.excerpt || !form.body) { toast.error("Title, excerpt and body are required."); return; }
    const payload = { title: form.title, excerpt: form.excerpt, body: form.body, category: form.category, cover_image: form.cover_image, author: form.author, published: form.published };
    try {
      if (editing) await api.put(`/admin/articles/${editing.id}`, payload);
      else await api.post("/admin/articles", payload);
      toast.success(editing ? "Article updated." : "Article created.");
      setDialogOpen(false); load();
    } catch { toast.error("Failed to save article."); }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/articles/${id}`); toast.success("Article deleted."); load();
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/admin/contacts/${id}`); toast.success("Message deleted."); load();
  };

  const markRead = async (id) => { await api.put(`/admin/contacts/${id}/read`); load(); };

  const doLogout = () => { logout(); navigate("/admin/login"); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const fmt = (d) => new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const unread = contacts.filter((c) => !c.read).length;

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-40">
        <div className="container-edge flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="IPNOMICS" className="h-8 w-8 object-contain" />
            <span className="font-display text-xl text-primary">IPNOMICS Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="text-sm text-muted-foreground hover:text-accent flex items-center gap-1">View site <ExternalLink size={14} /></a>
            <button data-testid="admin-logout-btn" onClick={doLogout} className="text-sm flex items-center gap-2 text-foreground hover:text-accent"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="container-edge py-10">
        <Tabs defaultValue="messages">
          <TabsList className="rounded-none mb-8">
            <TabsTrigger data-testid="tab-messages" value="messages" className="rounded-none gap-2"><Mail size={15} /> Messages {unread > 0 && <span className="ml-1 bg-accent text-white text-xs px-1.5 py-0.5">{unread}</span>}</TabsTrigger>
            <TabsTrigger data-testid="tab-articles" value="articles" className="rounded-none gap-2"><FileText size={15} /> Knowledge Bank</TabsTrigger>
          </TabsList>

          <TabsContent value="messages">
            <div className="border border-border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Subject</TableHead>
                    <TableHead className="w-[35%]">Message</TableHead><TableHead>Received</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No messages yet.</TableCell></TableRow>}
                  {contacts.map((c) => (
                    <TableRow key={c.id} data-testid={`message-row-${c.id}`} className={c.read ? "" : "bg-accent/5"} onClick={() => !c.read && markRead(c.id)}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell><a href={`mailto:${c.email}`} className="text-accent">{c.email}</a></TableCell>
                      <TableCell>{c.subject}</TableCell>
                      <TableCell className="text-sm text-foreground/70">{c.message}</TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmt(c.created_at)}</TableCell>
                      <TableCell><button data-testid={`delete-message-${c.id}`} onClick={(e) => { e.stopPropagation(); deleteContact(c.id); }} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="articles">
            <div className="flex justify-between items-center mb-6">
              <p className="text-muted-foreground text-sm">{articles.length} article(s)</p>
              <button data-testid="new-article-btn" onClick={openNew} className="btn-primary py-3 px-6"><Plus size={16} /> New Article</button>
            </div>
            <div className="border border-border bg-card">
              <Table>
                <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {articles.map((a) => (
                    <TableRow key={a.id} data-testid={`article-row-${a.id}`}>
                      <TableCell className="font-medium max-w-md">{a.title}</TableCell>
                      <TableCell>{a.category}</TableCell>
                      <TableCell>{a.published ? <span className="text-accent text-xs uppercase tracking-widest">Published</span> : <span className="text-muted-foreground text-xs uppercase tracking-widest">Draft</span>}</TableCell>
                      <TableCell className="flex gap-3">
                        <button data-testid={`edit-article-${a.id}`} onClick={() => openEdit(a)} className="text-muted-foreground hover:text-accent"><Pencil size={16} /></button>
                        <button data-testid={`delete-article-${a.id}`} onClick={() => deleteArticle(a.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="article-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit Article" : "New Article"}</DialogTitle><DialogDescription>Fill in the article details below. Body paragraphs are separated by blank lines.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><label className="overline block mb-1">Title</label><input data-testid="article-title-input" className="field" value={form.title} onChange={set("title")} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="overline block mb-1">Category</label><input className="field" value={form.category} onChange={set("category")} /></div>
              <div><label className="overline block mb-1">Author</label><input className="field" value={form.author} onChange={set("author")} /></div>
            </div>
            <div><label className="overline block mb-1">Cover Image URL</label><input className="field" value={form.cover_image || ""} onChange={set("cover_image")} placeholder="https://…" /></div>
            <div><label className="overline block mb-1">Excerpt</label><textarea data-testid="article-excerpt-input" rows={2} className="field resize-none" value={form.excerpt} onChange={set("excerpt")} /></div>
            <div><label className="overline block mb-1">Body (separate paragraphs with blank lines)</label><textarea data-testid="article-body-input" rows={8} className="field resize-none" value={form.body} onChange={set("body")} /></div>
            <div className="flex items-center gap-3"><Switch data-testid="article-published-switch" checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} /><span className="text-sm">Published</span></div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="btn-secondary py-3 px-6">Cancel</button>
            <button data-testid="save-article-btn" onClick={saveArticle} className="btn-primary py-3 px-6">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
